import { useCallback, useEffect, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button, F0Dialog, F0Select } from '@factorialco/f0-react'
import { AutoGrid } from '@factorialco/f0-react/dist/experimental'
import { Bell, Image, Megaphone, Phone, Settings, Video } from '@factorialco/f0-react/icons/app'
import { useLocalParticipant, useMediaDeviceSelect } from '@livekit/components-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import {
  VIRTUAL_BACKGROUND_TYPES,
  getVirtualBackgroundOptions,
} from 'modules/meetings/lib/constants/VirtualBackground'
import { useRoom } from 'modules/meetings/lib/hooks/useRoom'
import { useVirtualBackgroundSettings } from 'modules/meetings/lib/hooks/useVirtualBackgroundSettings'
import {
  applyVirtualBackground,
  isVirtualBackgroundSupported,
} from 'modules/meetings/lib/utils/virtualBackgroundUtils'

export const SettingsButton = () => {
  const [openModal, setOpenModal] = useState(false)
  const { soundsEnabled, setSoundsEnabled } = useRoom()
  const { localParticipant } = useLocalParticipant()
  const [backgroundSupported, setBackgroundSupported] = useState(false)
  const { backgroundType, setBackgroundType } = useVirtualBackgroundSettings()

  const {
    devices: audioInputDevices,
    activeDeviceId: activeAudioInputId,
    setActiveMediaDevice: setActiveAudioDevice,
  } = useMediaDeviceSelect({ kind: 'audioinput' })

  const {
    devices: videoInputDevices,
    activeDeviceId: activeVideoInputId,
    setActiveMediaDevice: setActiveVideoDevice,
  } = useMediaDeviceSelect({ kind: 'videoinput' })

  const {
    devices: audioOutputDevices,
    activeDeviceId: activeAudioOutputId,
    setActiveMediaDevice: setActiveAudioOutputDevice,
  } = useMediaDeviceSelect({ kind: 'audiooutput' })

  const isAudioOutputSelectionSupported = 'setSinkId' in HTMLMediaElement.prototype // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/setSinkId

  // Load saved audio output device from localStorage on mount
  useEffect(() => {
    const savedAudioOutputDeviceId = localStorage.getItem('livekit-audio-output-device')
    if (savedAudioOutputDeviceId && setActiveAudioOutputDevice) {
      setActiveAudioOutputDevice(savedAudioOutputDeviceId)
    }
  }, [setActiveAudioOutputDevice])

  // Check blur support on mount
  useEffect(() => {
    isVirtualBackgroundSupported().then((supported) => setBackgroundSupported(supported))
  }, [])

  const handleAudioInputChange = useCallback(
    async (deviceId: string) => {
      try {
        if (setActiveAudioDevice) {
          await setActiveAudioDevice(deviceId)
          // Enable microphone if it was disabled
          const micEnabled = localParticipant.isMicrophoneEnabled
          if (!micEnabled) {
            await localParticipant.setMicrophoneEnabled(true)
          }
        }
      } catch (error) {}
    },
    [localParticipant, setActiveAudioDevice]
  )

  const handleVideoInputChange = useCallback(
    async (deviceId: string) => {
      try {
        if (setActiveVideoDevice) {
          await setActiveVideoDevice(deviceId)
          // Enable camera if it was disabled
          const cameraEnabled = localParticipant.isCameraEnabled
          if (!cameraEnabled) {
            await localParticipant.setCameraEnabled(true)
          }
        }
      } catch (error) {}
    },
    [localParticipant, setActiveVideoDevice]
  )

  const handleAudioOutputChange = useCallback(
    async (deviceId: string) => {
      if (isAudioOutputSelectionSupported && setActiveAudioOutputDevice) {
        await setActiveAudioOutputDevice(deviceId)
        // Save to localStorage for persistence across sessions
        localStorage.setItem('livekit-audio-output-device', deviceId)
      }
    },
    [isAudioOutputSelectionSupported, setActiveAudioOutputDevice]
  )

  const handleSoundsChange = useCallback(
    (value: string) => {
      setSoundsEnabled(value === 'true')
    },
    [setSoundsEnabled]
  )

  const handleBackgroundChange = useCallback(
    async (value: string) => {
      const success = await applyVirtualBackground(localParticipant, value)
      if (success) {
        setBackgroundType(value)
      }
    },
    [localParticipant, setBackgroundType]
  )

  return (
    <>
      <F0Button
        label={i18n.t('meetings.room.control_bar.settings')}
        onClick={() => {
          track(trackingEvents.meetings.ControlBarSettingsButton)
          setOpenModal(true)
        }}
        icon={Settings}
        hideLabel
        variant="outline"
        size="lg"
      />

      <F0Dialog
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title={i18n.t('meetings.room.settings.title')}
        width="sm"
      >
        <Box flexDirection="column" gap="s20">
          <Text.Normal size="300" weight="medium" color="black">
            {i18n.t('meetings.room.settings.general_title')}
          </Text.Normal>
          <AutoGrid gap="4" tileSize="sm">
            <F0Select
              onChange={(deviceId: string) => {
                track(trackingEvents.meetings.SettingsModalAudioInputChange, {
                  deviceId,
                })
                handleAudioInputChange(deviceId)
              }}
              options={audioInputDevices.map((device) => ({
                label:
                  device.label ||
                  `${i18n.t(
                    'meetings.room.control_bar.microphone.label'
                  )} ${device.deviceId.substring(0, 5)}...`,
                value: device.deviceId,
              }))}
              placeholder={i18n.t('meetings.room.control_bar.microphone.placeholder')}
              searchBoxPlaceholder={i18n.t(
                'meetings.room.control_bar.microphone.search_placeholder'
              )}
              searchEmptyMessage={i18n.t(
                'meetings.room.control_bar.microphone.no_microphones_found'
              )}
              value={activeAudioInputId || ''}
              icon={Megaphone}
              label={i18n.t('meetings.room.settings.audio_input')}
            />

            {isAudioOutputSelectionSupported && (
              <F0Select
                onChange={(deviceId: string) => {
                  track(trackingEvents.meetings.SettingsModalAudioOutputChange, {
                    deviceId,
                  })
                  handleAudioOutputChange(deviceId)
                }}
                options={audioOutputDevices.map((device) => ({
                  label:
                    device.label ||
                    `${i18n.t(
                      'meetings.room.control_bar.speakers.label'
                    )} ${device.deviceId.substring(0, 5)}...`,
                  value: device.deviceId,
                }))}
                placeholder={i18n.t('meetings.room.control_bar.speakers.placeholder')}
                searchBoxPlaceholder={i18n.t(
                  'meetings.room.control_bar.speakers.search_placeholder'
                )}
                searchEmptyMessage={i18n.t('meetings.room.control_bar.speakers.no_speakers_found')}
                value={activeAudioOutputId || ''}
                icon={Phone}
                label={i18n.t('meetings.room.settings.audio_output')}
              />
            )}

            <F0Select
              onChange={(deviceId: string) => {
                track(trackingEvents.meetings.SettingsModalVideoInputChange, {
                  deviceId,
                })
                handleVideoInputChange(deviceId)
              }}
              options={videoInputDevices.map((device) => ({
                label:
                  device.label ||
                  `${i18n.t('meetings.room.control_bar.camera.label')} ${device.deviceId.substring(
                    0,
                    5
                  )}...`,
                value: device.deviceId,
              }))}
              placeholder={i18n.t('meetings.room.control_bar.camera.placeholder')}
              searchBoxPlaceholder={i18n.t('meetings.room.control_bar.camera.search_placeholder')}
              searchEmptyMessage={i18n.t('meetings.room.control_bar.camera.no_cameras_found')}
              value={activeVideoInputId || ''}
              icon={Video}
              label={i18n.t('meetings.room.settings.video_input')}
            />
          </AutoGrid>
          <Text.Normal size="300" weight="medium" color="black">
            {i18n.t('meetings.room.settings.effects_title')}
          </Text.Normal>
          <AutoGrid gap="4" tileSize="sm">
            <F0Select
              onChange={(value: string) => {
                track(trackingEvents.meetings.SettingsModalSoundEffectsChange, {
                  value,
                })
                handleSoundsChange(value)
              }}
              options={[
                {
                  label: i18n.t('meetings.room.settings.interactive_sounds.enabled'),
                  value: 'true',
                },
                {
                  label: i18n.t('meetings.room.settings.interactive_sounds.disabled'),
                  value: 'false',
                },
              ]}
              placeholder={i18n.t('meetings.room.settings.interactive_sounds.placeholder')}
              searchBoxPlaceholder={i18n.t(
                'meetings.room.settings.interactive_sounds.search_placeholder'
              )}
              searchEmptyMessage={i18n.t(
                'meetings.room.settings.interactive_sounds.no_sounds_found'
              )}
              value={soundsEnabled.toString()}
              icon={Bell}
              label={i18n.t('meetings.room.settings.interactive_sounds.label')}
            />

            {backgroundSupported && (
              <F0Select
                onChange={(value: string) => {
                  track(trackingEvents.meetings.SettingsModalBackgroundChange, {
                    value,
                  })
                  handleBackgroundChange(value)
                }}
                options={getVirtualBackgroundOptions()}
                placeholder={i18n.t('meetings.room.settings.blur_background.placeholder')}
                searchBoxPlaceholder={i18n.t(
                  'meetings.room.settings.blur_background.search_placeholder'
                )}
                searchEmptyMessage={i18n.t(
                  'meetings.room.settings.blur_background.no_backgrounds_found'
                )}
                value={backgroundType || VIRTUAL_BACKGROUND_TYPES.DISABLED}
                icon={Image}
                label={i18n.t('meetings.room.settings.blur_background.label')}
              />
            )}
          </AutoGrid>
        </Box>
      </F0Dialog>
    </>
  )
}
